/**
====================================
=====================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from 'react';

// react-router components
import { useLocation, Link } from 'react-router-dom';

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types';

// @material-ui core components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDBadge from 'components/MDBadge';

// Material Dashboard 2 PRO React examples
import Breadcrumbs from 'examples/Breadcrumbs';
import NotificationItem from 'examples/Items/NotificationItem';

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from 'examples/Navbars/DashboardNavbar/styles';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav, setOpenConfigurator } from 'context';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useAuth } from '../../../../contexts/AuthContext';
import { Divider, Typography } from '@mui/material';
import UserProfile from '../../../../components/user-management/UserProfile';
import CreateUserDialog from '../../../../components/user-management/CreateUserDialog';
import { InputAdornment, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProjectCompsDialog from '../../../../components/project-comps/ProjectCompsDialog';
function DashboardNavbar({ absolute, light, isMini, navbarButtons, title, pageHeader }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split('/').slice(1);
  const { logout, isAuthenticated, user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [isProfileDialogOpen, setProfileDialogOpen] = useState(false);
  const [isCreateUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    document.title = pageHeader;
  }, [pageHeader]);

  const handleSearchInputChange = event => {
    setSearchQuery(event.target.value);
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSearchQuery('');
  };

  const clearSearchQuery = () => {
    setSearchQuery('');
  };

  const handleOpenProfile = () => {
    navigate('/user-profile');
    setOpenMenu(false);
  };

  const handleManageUsers = () => {
    navigate('/users');
    setOpenMenu(false);
  };

  if (!title) title = route[route.length - 1];

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType('sticky');
    } else {
      setNavbarType('static');
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener('scroll', handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener('scroll', handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = event => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleLogout = async () => {
    try {
      if (isAuthenticated) {
        await logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const renderMenu = () => (
    <Menu
      className='user-profile'
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {/* Add user information if available */}
      {user && (
        <ListItemText
          secondary={user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName}
          style={{ textAlign: 'center' }} // Customize the color here
        />
      )}
      <Divider sx={{ my: 1 }} /> {/* Add a Divider with margin */}
      <NotificationItem icon={<Icon>person</Icon>} title='Profile' onClick={handleOpenProfile} />
      {hasRole('System Admin') && (
        <NotificationItem icon={<Icon>admin_panel_settings</Icon>} title='Manage Users' onClick={handleManageUsers} />
      )}
      <NotificationItem icon={<Icon>logout</Icon>} title='Logout' onClick={handleLogout} />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? 'absolute' : navbarType}
      color='inherit'
      sx={theme => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={theme => navbarContainer(theme)}>
        <MDBox color='inherit' mb={{ xs: 1, md: 0 }} sx={theme => navbarRow(theme, { isMini })}>
          {navbarButtons.map(button =>
            button.text === 'Toggle Sidebar' ? (
              <IconButton key={button.text} size='small' disableRipple color='inherit' sx={navbarIconButton} onClick={button.onClick}>
                <Icon sx={iconsStyle}>{button.icon}</Icon>
              </IconButton>
            ) : null,
          )}
          <Breadcrumbs icon='home' title={pageHeader || title} route={route} light={light} />
          {/* <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size='small' disableRipple>
            <Icon fontSize='medium' sx={iconsStyle}>
              {miniSidenav ? 'menu_open' : 'menu'}
            </Icon>
          </IconButton> */}
        </MDBox>
        {/* <MDBox>
          <Typography variant='h6' color='primary' textTransform='uppercase' mr={9}>
            {pageHeader}
          </Typography>
        </MDBox> */}
        {isMini ? null : (
          <MDBox sx={theme => navbarRow(theme, { isMini })}>
            {/* <MDBox pr={1}>
              <MDInput label='Search here' />
            </MDBox> */}
            <MDBox color={light ? 'white' : 'inherit'}>
              {pageHeader !== 'Projects List' && (
                <TextField
                  label='Search Projects'
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleKeyDown}
                  variant='standard'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        {searchQuery ? (
                          <IconButton onClick={clearSearchQuery} size='small'>
                            <Clear />
                          </IconButton>
                        ) : (
                          <Search fontSize='small' />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{ alignSelf: 'flex-end !important', fontSize: 'large', verticalAlign: 'bottom', marginRight: 3 }}
                />
              )}
              <ProjectCompsDialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                projectSearchQuery={searchQuery}
                isFromReport={true}
                disableChecboxSelectionOnMaps
              />
              {navbarButtons
                .filter(button => button.text !== 'Toggle Sidebar')
                .map(button => (
                  <IconButton key={button.text} size='small' disableRipple color='inherit' sx={navbarIconButton} onClick={button.onClick}>
                    <Icon sx={iconsStyle}>{button.icon}</Icon>
                  </IconButton>
                ))}
              {/* <Link to='/authentication/sign-in/basic'>
                <IconButton sx={navbarIconButton} size='small' disableRipple>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link> */}
              {/* <IconButton
                key='mobileSideNavToggle'
                size='small'
                disableRipple
                color='inherit'
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize='medium'>
                  {miniSidenav ? 'menu_open' : 'menu'}
                </Icon>
              </IconButton> */}

              {/* <IconButton size='small' disableRipple color='inherit' sx={navbarIconButton} onClick={handleConfiguratorOpen}>
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> */}
              <IconButton
                key={'userMenu'}
                size='small'
                disableRipple
                color='inherit'
                sx={navbarIconButton}
                aria-controls='notification-menu'
                aria-haspopup='true'
                variant='contained'
                onClick={handleOpenMenu}
              >
                {/* <MDBadge badgeContent={9} color='error' size='xs' circular>
                </MDBadge> */}
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
